import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { Assessment, Refresh, Search, Security, Restore, SettingsApplications } from '@mui/icons-material';
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { db } from '../../config/firebase';
import BrandedPageLoader from '../common/BrandedPageLoader';
import {
  PanelTitle, StatusChip, WorkspaceHeader, WorkspacePage, WorkspacePanel, WorkspaceStatCard,
  fieldSx, quietButtonSx, tableContainerSx, workspacePalette,
} from '../common/AdminWorkspace';

const asDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};
const fmt = (value) => asDate(value)?.toLocaleString() || '—';
const words = (value) => String(value || 'unknown').replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [module, setModule] = useState('all');

  useEffect(() => {
    const q = query(collection(db, 'auditLogs'), orderBy('createdAt', 'desc'), limit(500));
    return onSnapshot(q, (snap) => {
      setRows(snap.docs.map((row) => ({ id: row.id, ...row.data() })));
      setLoading(false);
    }, (error) => {
      console.error('Audit log listener:', error);
      setLoading(false);
      toast.error('Unable to load the audit history.');
    });
  }, []);

  const modules = useMemo(() => Array.from(new Set(rows.map((row) => String(row.module || row.entityType || 'system')).filter(Boolean))).sort(), [rows]);
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows.filter((row) => {
      const rowModule = String(row.module || row.entityType || 'system');
      if (module !== 'all' && rowModule !== module) return false;
      if (!needle) return true;
      return [row.action, row.module, row.entityType, row.entityId, row.actorId, row.previousStatus, row.nextStatus, row.companyId, row.userId]
        .some((value) => String(value || '').toLowerCase().includes(needle));
    });
  }, [rows, search, module]);

  const today = useMemo(() => {
    const start = new Date(); start.setHours(0, 0, 0, 0);
    return rows.filter((row) => (asDate(row.createdAt)?.getTime() || 0) >= start.getTime()).length;
  }, [rows]);
  const actors = useMemo(() => new Set(rows.map((row) => row.actorId).filter(Boolean)).size, [rows]);

  if (loading) return <WorkspacePage><BrandedPageLoader label="Loading audit history…" skeleton /></WorkspacePage>;

  return <WorkspacePage>
    <WorkspaceHeader
      eyebrow="Governance"
      title="Audit Log"
      subtitle="Review critical platform changes, payment actions, moderation decisions and administrative events."
      icon={<Assessment />}
      accent={workspacePalette.purple}
      action={<Button startIcon={<Refresh />} sx={quietButtonSx} onClick={() => toast.success('Audit history is updating live.')}>Refresh view</Button>}
    />
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3,1fr)' }, gap: 1.15, mb: 1.25 }}>
      <WorkspaceStatCard label="Audit events" value={rows.length} hint="Latest 500 events" icon={<Restore />} accent={workspacePalette.purple} />
      <WorkspaceStatCard label="Today" value={today} hint="Events since midnight" icon={<Security />} accent={workspacePalette.green} />
      <WorkspaceStatCard label="Administrators" value={actors} hint="Recorded actors" icon={<SettingsApplications />} accent={workspacePalette.blue} />
    </Box>
    <WorkspacePanel accent={workspacePalette.purple}>
      <PanelTitle title="Activity History" subtitle={`${filtered.length} events`} icon={<Assessment />} accent={workspacePalette.purple} />
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.1 }}>
        <TextField
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search action, entity or administrator"
          InputProps={{ startAdornment: <Search sx={{ mr: .7, color: workspacePalette.inkFaint, fontSize: 18 }} /> }}
          sx={{ ...fieldSx, width: { xs: '100%', md: 390 } }}
        />
        <TextField select label="Module" value={module} onChange={(event) => setModule(event.target.value)} sx={{ ...fieldSx, minWidth: 190 }}>
          <MenuItem value="all">All modules</MenuItem>
          {modules.map((item) => <MenuItem key={item} value={item}>{words(item)}</MenuItem>)}
        </TextField>
      </Box>
      <Box sx={tableContainerSx}>
        <Table size="small">
          <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Module</TableCell><TableCell>Action</TableCell><TableCell>Entity</TableCell><TableCell>Actor</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
          <TableBody>
            {filtered.map((row) => <TableRow key={row.id} hover>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmt(row.createdAt)}</TableCell>
              <TableCell><StatusChip label={words(row.module || row.entityType || 'system')} color={workspacePalette.blue} /></TableCell>
              <TableCell><Typography sx={{ fontSize: '.7rem', fontWeight: 850, color: workspacePalette.ink }}>{words(row.action)}</Typography></TableCell>
              <TableCell><Typography sx={{ fontSize: '.66rem', color: workspacePalette.inkSoft }}>{words(row.entityType)}{row.entityId ? ` · ${row.entityId}` : ''}</Typography></TableCell>
              <TableCell sx={{ fontFamily: 'monospace', fontSize: '.63rem' }}>{row.actorId || 'system'}</TableCell>
              <TableCell>{row.nextStatus ? <StatusChip label={words(row.nextStatus)} color={row.nextStatus === 'paid' || row.nextStatus === 'active' ? workspacePalette.green : workspacePalette.amber} /> : '—'}</TableCell>
            </TableRow>)}
            {!filtered.length && <TableRow><TableCell colSpan={6}><Box sx={{ py: 5, textAlign: 'center' }}><Assessment sx={{ fontSize: 34, color: workspacePalette.inkFaint }} /><Typography sx={{ mt: .8, fontWeight: 850, color: workspacePalette.ink }}>No matching audit events</Typography><Typography sx={{ fontSize: '.68rem', color: workspacePalette.inkLight }}>Critical administrative activity will appear here.</Typography></Box></TableCell></TableRow>}
          </TableBody>
        </Table>
      </Box>
    </WorkspacePanel>
  </WorkspacePage>;
}
